import {
  getDimensionLabel,
} from 'components/DimensionLabel';
import { getDimensionValue } from 'components/DimensionValue';
import { fraction } from 'shared/constants/math';
import { getMetricLabel } from 'components/MetricLabel';
import { getFormatter, percentFormat } from 'shared/utils/formatter';
import { getMetricPrecision } from 'shared/utils/metricPrecision';
import { metricSupportsChange } from 'shared/utils/metricChange';
import { dayjs } from 'shared/utils/date';
import { getRowId, getSegmentHtmlCell } from './utils';

const formattedMetricValue = ({ results, segment, dimensionIds, rowId, metric, currency }) => {
  const value = results
    ?.find((r) => r.segmentId === segment.id)
    ?.data.find((d) => getRowId(d, dimensionIds) === rowId)?.[metric?.name];

  let s = `<div class="metric-value">${getFormatter(metric?.type || 'number', { currency, maximumFractionDigits: getMetricPrecision(metric, 'tooltip') })(value)}</div>`;

  if (metricSupportsChange(metric) && segment.dateRangeSegments?.length === 1) {
    for (const comparison of segment.dateRangeSegments) {
      const comparisonValue = results
        ?.find((r) => r.segmentId === comparison.id)
        ?.data.find((d) => getRowId(d, dimensionIds) === rowId)?.[metric?.name];
      const percentageChange = fraction(
        value - comparisonValue,
        comparisonValue,
      );
      const isPositive = metric.negative
        ? percentageChange < 0
        : percentageChange > 0;
      const isNegative = metric.negative
        ? percentageChange > 0
        : percentageChange < 0;

      s += `
        <div class="metric-change ${isPositive ? 'positive' : ''} ${isNegative ? 'negative' : ''}">
          <span class="noto">
          ${percentageChange > 0 ? '🡑' : ''}
          ${percentageChange < 0 ? '🡓' : ''}
          </span>
          ${percentFormat(percentageChange)}
        </div>
      `;
    }
  } else if (metricSupportsChange(metric)) {
    s += '<div class="metric-change"></div>';
  }

  return `<div class="metric">${s}</div>`;
};

export const tooltipFormatter = ({ title, description = '', headers, rows }) => `
  <div class="chart-tooltip-body">
    ${title ? `<div class="chart-tooltip-title">${title}</div>` : ''}
    ${description}
    <div class="chart-tooltip-scroll">
      <table class="chart-tooltip-table">
        <thead>
          <tr>
            ${headers?.map((h) => `<th>${h}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${rows?.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join('')}</tr>`).join('')}
        </tbody>
      </table>
    </div>
  </div>
`;

export const metricsTooltipFormatter = ({
  $t,
  segments,
  metrics,
  results,
  dimensions,
  rowId,
  date,
  goalNames,
  goalNamesLoading,
  currency,
}) => {
  let title;
  const dimensionIds = dimensions.map(d => d.name);

  if (date) {
    title = dayjs(date).format('YYYY-MM-DD');
    dimensionIds.push('date');
  }

  const row = results[0].data.find(d => getRowId(d, dimensionIds) === rowId);

  const description = dimensions.map(d => `
    <div>
      <b>${getDimensionLabel({ $t, name: d.name })}:</b>
      ${getDimensionValue({ $t, value: row?.[d.name], dimension: d, goalNames, goalNamesLoading, html: true })}
    </div>
  `).join('');
  const headers = [
    'Segment',
    ...metrics.map((m) => getMetricLabel({ $t, name: m.name })),
  ];
  const rows = segments.map((s) => [
    getSegmentHtmlCell(s),
    ...metrics.map((m) => formattedMetricValue({
      results,
      segment: s,
      dimensionIds,
      rowId,
      metric: m,
      currency,
    })),
  ]);

  return tooltipFormatter({ title, description, headers, rows });
};
